import { Component, OnInit, Input, SimpleChanges, Output, EventEmitter, ViewChild, OnChanges } from '@angular/core';

// 3rd party imports
import notify from 'devextreme/ui/notify';
import * as moment from 'moment-timezone';
import { DxPopupComponent } from 'devextreme-angular';

// cc-imports
import { IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// Service imports
import { VendorAccountEditService } from '../../_services/vendor-account-edit.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { HelperService } from '../../../_services/shared-services/helper.service';

// Constant Imports
import { MessageInterval } from '../../../_constants/messages-intervals';

// Model imports
import { VendorAccountRawDto } from '../../_models/vendor-account-raw-dto';
import { VendorAccountEditorResultDto } from '../../_models/vendor-account-editor-result-dto';
import { VendorAccountParametersDto } from '../../_models/vendor-account-parameters-dto';
import { VendorAccountEFTFormParametersDto } from '../../_models/vendor-account-eft-form-parameters-dto';
import { FileInfoDisplayResultDto } from '../../../_models/file-info-display-result-dto';
import { StatusMessageResultDto } from '../../../_models/status-message-result-dto';
import { VendorAccountResultDto } from '../../_models/vendor-account-result-dto';

@Component({
    selector: 'app-vendor-account-editor',
    templateUrl: './vendor-account-editor.component.html',
    styleUrls: ['./vendor-account-editor.component.scss']
})
export class VendorAccountEditorComponent implements OnInit, OnChanges {
    @ViewChild('vendorAccountMessageBox', { static: false }) vendorAccountMessageBox: MessageBoxComponent;
    @ViewChild('vendorAccountEditorPopup', { static: false }) vendorAccountEditorPopup: DxPopupComponent;

    @Input() communityId: number;
    @Input() vendorName: string;
    @Input() associationVendorId: number;
    @Input() vendorAccount: VendorAccountResultDto;
    @Input() configMode: string;
    @Input() title: string;

    @Output() closeVendorAccount = new EventEmitter<boolean>();

    vendorAccountRaw: VendorAccountRawDto;
    vendorAccountEditor: VendorAccountEditorResultDto;

    showManageDocuments = false;
    showNotesPopUp = false;
    enableDateBilling = false;
    loadingVisible = false;
    bankAccountsBoxValue: number[];
    bankAccountColumns: any[];
    vendorAccountBankAccounts: any = [];
    vendorAccountStatusUpdatedAt: string;
    billingDateValue: Date;

    visible = false;
    popupHeight = '82%';
    toForceRefresh = false;

    iconNotes: string = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes: string = IconConstants.NOTES_EMPTY;
    iconTimes = IconConstants.CROSS;
    iconSave = IconConstants.SAVE;
    iconPdf = IconConstants.PDF_FILE;

    constructor(
        private readonly vendorAccountEditService: VendorAccountEditService,
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService
    ) {}

    ngOnInit() {
        this.showEditor();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.communityId && changes.communityId.currentValue) {
            this.communityId = changes.communityId.currentValue;
        }

        if (changes.vendorName && changes.vendorName.currentValue) {
            this.vendorName = changes.vendorName.currentValue;
        }

        if (changes.associationVendorId && changes.associationVendorId.currentValue) {
            this.associationVendorId = changes.associationVendorId.currentValue;
        }

        if (changes.vendorAccount && changes.vendorAccount.currentValue) {
            this.vendorAccount = changes.vendorAccount.currentValue;
        }

        if (changes.configMode && changes.configMode.currentValue) {
            this.configMode = changes.configMode.currentValue;
            this.checkConfigState();
        }
    }

    onVendorAccountEditorPopupShown(): void {
        this.visible = true;
        this.vendorAccountEditorPopup.instance.repaint();
    }

    onEFTActiveStateChanged(): void {
        if (Utils.isTrue(this.vendorAccountRaw.eftActive)) {
            this.vendorAccountRaw.eftInactiveReason = null;
        } else {
            this.vendorAccountRaw.bankAccountId = null;
            this.bankAccountsBoxValue = [];
        }
    }

    onCancelVendorAccountConfiguration(): void {
        this.resetAllPopups();
        this.closeAndRefresh();
    }

    onSaveVendorAccountConfiguration(): void {
        const errorMsg = this.validateRequiredFields();
        if (Utils.notNullOrEmpty(errorMsg)) {
            this.vendorAccountMessageBox.showErrorMessage('Error', `${errorMsg} is required.`);
        } else {
            this.resetAllPopups();
            this.saveVendorAccount();
        }
    }

    onEFTFormClick(): void {
        if (this.vendorAccountEditor.eftForms.length === 1) {
            const form = this.vendorAccountEditor.eftForms[0];
            this.getEFTForm(form);
        } else {
            this.showManageDocuments = true; // Open Manage Document Pop up here..
        }
    }

    onManageDocumentsClose(): void {
        this.showManageDocuments = false;
    }

    onDownloadFile(row): void {
        this.getEFTForm(row);
    }

    onBillingFrequencyChange(): void {
        this.enableDateBillingValueType();
    }

    onNotesClick(): void {
        this.showNotesPopUp = true;
    }

    onVendorAccountNotesClose(event): void {
        if (event) {
            this.vendorAccountRaw.hasNotes = true;
            this.toForceRefresh = true;
        }
        this.showNotesPopUp = false;
    }

    onBillingValueDateChanged(): void {
        if (this.billingDateValue) {
            const formattedDate = this.helperService.getDateString(new Date(this.billingDateValue));
            this.vendorAccountRaw.billingFrequencyValue = formattedDate;
        } else {
            this.vendorAccountRaw.billingFrequencyValue = null;
        }
    }

    onBankAccountSelection(event: any): void {
        if (Utils.notNullOrEmpty(event.selectedRow)) {
            const selectedRow = event.selectedRow[0];
            this.vendorAccountRaw.bankAccountId = selectedRow.bankAccountId;
        } else {
            this.vendorAccountRaw.bankAccountId = null;
        }
    }

    onAddEditAccountClose(): void {
        this.visible = false;
        this.closeVendorAccount.emit(false);
    }

    private showEditor(): void {
        this.setPopupHeight();

        setTimeout(() => {
            this.visible = true;
            this.getVendorConfiguration();
        }, 200);
    }

    private validateRequiredFields(): string {
        let errorMsg = '';
        if (Utils.isNullOrEmpty(this.vendorAccountRaw.accountNumber)) {
            errorMsg = 'Account Number';
        }
        if (Utils.isNullOrEmpty(this.vendorAccountRaw.description)) {
            errorMsg = `${errorMsg} Description,`;
        }
        if (
            Utils.isTrue(this.vendorAccountRaw.eftActive) &&
            Utils.isNullOrUndefined(this.vendorAccountRaw.bankAccountId)
        ) {
            errorMsg = `${errorMsg} Bank Account Id,`;
        }
        return errorMsg;
    }

    private getEFTForm(eftForm: FileInfoDisplayResultDto): void {
        const baseParams = new VendorAccountEFTFormParametersDto();
        baseParams.associationVendorId = this.associationVendorId;
        baseParams.communityId = this.communityId;
        baseParams.eftFormId = eftForm.fileId;

        this.downloadEFTForm(baseParams, eftForm.fileName);
    }

    private downloadEFTForm(params, fileName: string): void {
        this.loadingVisible = true;
        this.vendorAccountEditService.getVendorAccountEFTForm(params).subscribe(
            (result: Blob) => {
                this.helperService.downloadFileUsingBlob(result, fileName);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.updateSubjectErrorMessage('File not available.');
                this.loadingVisible = false;
            }
        );
    }

    private checkConfigState(): void {
        if (this.configMode === 'add') {
            if (!this.vendorAccountRaw) {
                this.getVendorConfiguration();
            }
        }
    }

    private enableDateBillingValueType(): void {
        if (this.vendorAccountRaw.billingFrequency === 'AdHocOther') {
            this.enableDateBilling = false;
        } else {
            this.enableDateBilling = true;
        }
    }

    private resetComponents(): void {
        this.vendorAccountEditor = null;
        this.vendorAccountRaw = null;
        this.bankAccountsBoxValue = null;
        this.vendorAccountStatusUpdatedAt = null;
        this.billingDateValue = null;
    }

    private getVendorConfiguration(): void {
        this.resetComponents();
        this.getBankAccountColumns();

        if (this.associationVendorId) {
            this.getVendorAccountEditor();
        }
    }

    private getBankAccountColumns(): void {
        this.bankAccountColumns = [
            {
                dataField: 'bankName',
                dataType: 'string',
                caption: 'Bank Account'
            },
            {
                dataField: 'glAccountNumber',
                dataType: 'string',
                caption: 'GL Account Number'
            },
            {
                dataField: '',
                dataType: 'string',
                caption: 'GL Dimensions',
                cellTemplate: 'dimensionTemplate'
            },
            {
                dataField: 'isDefault',
                dataType: 'boolean',
                caption: 'Is Default',
                cellTemplate: 'trueValueCellTemplate'
            }
        ];
    }

    private getVendorAccountEditor(): void {
        const baseParams = new VendorAccountParametersDto();
        baseParams.communityId = this.communityId;
        baseParams.associationVendorId = this.associationVendorId;

        if (this.vendorAccount) {
            baseParams.vendorAccountId = this.vendorAccount.id;
        }

        this.loadingVisible = true;
        this.vendorAccountEditService.getVendorAccountEditor(baseParams).subscribe(
            (result: VendorAccountEditorResultDto) => {
                this.vendorAccountEditor = result;
                this.vendorAccountRaw = result.vendorAccount;
                this.setDefaultComponentValues();
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private setDefaultComponentValues(): void {
        this.setPopupHeight();
        this.enableDateBillingValueType();

        if (this.configMode === 'add') {
            this.vendorAccountRaw.active = true;
            this.vendorAccountRaw.eftActive = false;
            this.vendorAccountRaw.agreeToVendorTerms = false;
            this.vendorAccountRaw.chargesLateFeeOrFinanceCharge = true;
            this.vendorAccountRaw.willTerminateOrDisconnect = true;
        }

        if (this.vendorAccountRaw.bankAccountId) {
            this.bankAccountsBoxValue = [this.vendorAccountRaw.bankAccountId];
        }

        if (Utils.isNullOrEmpty(this.vendorAccountRaw.billingFrequency)) {
            const defaultBillingFrequency = this.vendorAccountEditor.billingFrequencies.find((d) =>
                Utils.isTrue(d.isDefault)
            );
            this.vendorAccountRaw.billingFrequency = Utils.notNullAndDefined(defaultBillingFrequency)
                ? defaultBillingFrequency.key
                : null;
        }

        if (this.vendorAccountRaw.billingFrequencyValue) {
            if (this.vendorAccountRaw.billingFrequency && this.vendorAccountRaw.billingFrequency !== 'AdHocOther') {
                const dd = this.vendorAccountRaw.billingFrequencyValue;
                const nowDate = new Date();
                nowDate.setDate(parseInt(dd));
                this.billingDateValue = new Date(nowDate);
            }
        }

        if (this.vendorAccountEditor.bankAccounts.length > 0) {
            this.vendorAccountBankAccounts = [];
            const bankAccounts = this.vendorAccountEditor.bankAccounts;
            bankAccounts.forEach((bank) => {
                bank.glDimensions.forEach((dimension) => {
                    const obj: any = {};
                    obj.bankAccountId = bank.bankAccountId;
                    obj.bankName = bank.bankName;
                    obj.accountNumber = bank.accountNumber;
                    obj.glAccountNumber = bank.glAccountNumber;
                    obj.dimensionTitle = dimension.dimensionTitle;
                    obj.dimensionOptionTitle = dimension.dimensionOptionTitle;
                    obj.isDefault = bank.isDefault;

                    this.vendorAccountBankAccounts.push(obj);
                });
            });
        }

        if (this.vendorAccountRaw.statusLastUpdated) {
            const formattedDate = moment
                .tz(this.vendorAccountRaw.statusLastUpdated, this.helperService.getUserTimezone())
                .format('MM/DD/YYYY hh:mm A');
            this.vendorAccountStatusUpdatedAt = formattedDate;
            this.vendorAccountRaw.statusLastUpdated = new Date(formattedDate);
        }
    }

    private checkVendorAccountStatus(): boolean {
        let status = false;
        let statusMessage: string = null;
        if (this.configMode === 'edit') {
            if (!this.vendorAccountRaw.id) {
                statusMessage = 'Vendor Account id is required';
            }
        }
        if (this.vendorAccountRaw.isEFTOffered) {
            if (!this.vendorAccountRaw.eftActive && !this.vendorAccountRaw.eftInactiveReason) {
                statusMessage = 'Vendor Account EFT Inactive Reason is required.';
            }
        }

        if (!statusMessage) {
            status = true;
        } else {
            this.vendorAccountMessageBox.showErrorMessage('Error', statusMessage);
        }

        return status;
    }

    private saveVendorAccount(): void {
        if (this.checkVendorAccountStatus()) {
            this.loadingVisible = true;
            this.vendorAccountEditService.saveVendorAccount(this.vendorAccountRaw).subscribe(
                (result: StatusMessageResultDto) => {
                    if (result.isSuccessMessage) {
                        notify(
                            Utils.equals(this.configMode, 'edit')
                                ? 'Vendor account has been updated successfully.'
                                : 'Vendor account has added successfully.',
                            'success',
                            MessageInterval.successMsgInterval
                        );
                        this.toForceRefresh = true;
                        this.closeAndRefresh();
                    } else {
                        notify(result.message, 'error');
                    }
                    this.loadingVisible = false;
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        }
    }

    private closeAndRefresh(): void {
        this.resetComponents();
        this.closeVendorAccount.emit(this.toForceRefresh);
    }

    private setPopupHeight(): void {
        if (Utils.notNullAndDefined(this.vendorAccountRaw) && Utils.isTrue(this.vendorAccountRaw.isEFTOffered)) {
            this.popupHeight = '95%';
        }
    }

    private resetAllPopups(): void {
        this.showManageDocuments = false;
        this.showNotesPopUp = false;
    }
}
